import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  loginUser,
  sendPasswordResetToken,
  registerOrganization,
} from "../../redux/action/authAction/AuthAction";
import { loginErrorToMessage, VIEW, VIEW_COPY } from "../../utils/Constant";
import Toaster, { notify } from "../../common/Toast";
import InventoryGrid from "../../common/InventoryGrid";
import ParticleField from "../../common/ParticleField";
import RegisterOrgForm from "./AuthForm/RegisterOrgForm";
import ForgotPasswordForm from "./AuthForm/ForgotPassword";
import LoginForm from "./AuthForm/LoginForm";
import {
  containerVariants,
  itemVariants,
  viewSwitchVariants,
} from "../../common/MotionVariants";
import { LuArrowLeft } from "react-icons/lu";

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    isAuthenticated,
    isLoading,
    error,
    forgotPasswordLoading,
    registerOrgLoading,
  } = useSelector((state) => state.auth);

  const [view, setView] = useState(VIEW.LOGIN);
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = location.state?.from?.pathname || "/";
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated]);

  const handleLogin = async (email, password) => {
    setLoginError("");
    const result = await dispatch(loginUser(email, password));
    if (result && !result.success) {
      setLoginError(loginErrorToMessage(result.error));
    }
  };

  const handleForgotPassword = async (email) => {
    const result = await dispatch(sendPasswordResetToken(email));
    if (result?.success) {
      notify.success(
        "Password reset email sent",
        "Check your inbox for the reset token.",
      );
      setView(VIEW.LOGIN);
    } else {
      notify.error("Failed to send password reset email", result?.error);
    }
  };

  const handleRegisterOrg = async (form) => {
    const result = await dispatch(registerOrganization(form));
    if (result?.success) {
      notify.success(
        "Organization registered",
        "Use the token sent to your email to set your password.",
      );
      setView(VIEW.LOGIN);
    } else {
      notify.error("Registration failed", result?.error);
    }
  };

  const copy = VIEW_COPY[view];

  return (
    <div className="relative flex min-h-screen w-full overflow-hidden bg-[#0A0F1A] font-['IBM_Plex_Sans']">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 10%, rgba(54,82,255,0.16), transparent 60%), radial-gradient(50% 40% at 85% 80%, rgba(109,58,235,0.12), transparent 60%)",
        }}
      />

      <aside className="relative hidden w-[44%] flex-col justify-between overflow-hidden lg:flex">
        <ParticleField />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="relative z-10 flex h-full flex-col justify-between"
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-2.5 px-10 pt-10"
          >
            <div className="relative">
              <motion.div
                animate={{ opacity: [0.25, 0.5, 0.25], scale: [1, 1.15, 1] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 -z-10 rounded-full bg-[#5B7CFF]/30 blur-xl"
              />
              <img
                src="https://mediafiles.projects.oceanicstudio.net/inads-cms-staging/ksas/logo_image/1709961414_logo-wht.png"
                alt="KSAS"
                className="h-11 w-auto"
              />
            </div>
            <span className="h-4 w-px bg-white/15" />
            <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Lab Inventory
            </span>
          </motion.div>

          <div className="px-10">
            {/* <motion.p
              variants={itemVariants}
              className="font-['IBM_Plex_Mono'] text-xs uppercase tracking-[0.3em] text-[#5B7CFF]"
            >
              Stock &middot; Track &middot; Reorder
            </motion.p> */}
            <motion.h2
              variants={itemVariants}
              className="mt-3 max-w-sm text-4xl font-semibold leading-[1.15] tracking-tight text-white"
            >
              Know what's on the{" "}
              <span className="bg-gradient-to-r from-[#8CA0FF] via-white to-[#8CA0FF] bg-[length:200%_100%] bg-clip-text text-transparent [animation:shimmer_5s_linear_infinite]">
                shelf
              </span>
              , before it runs out.
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400"
            >
              KSAS keeps every reagent, consumable, and piece of equipment
              visible — stock levels, expiry dates, and reorder points, all in
              one place.
            </motion.p>
          </div>

          <motion.div
            variants={itemVariants}
            className="border-t border-white/[0.06] px-10 py-9"
          >
            <InventoryGrid />
          </motion.div>
        </motion.div>
      </aside>

      <main className="relative z-10 flex w-full flex-1 items-center justify-center px-6 py-12 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <div className="mb-8 flex items-center justify-center gap-2.5 lg:hidden">
            <img
              src="https://mediafiles.projects.oceanicstudio.net/inads-cms-staging/ksas/logo_image/1709961414_logo-wht.png"
              alt="KSAS"
              className="h-7 w-auto"
            />
            <span className="h-4 w-px bg-white/15" />
            <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Lab Inventory
            </span>
          </div>

          <motion.div
            whileHover={{ boxShadow: "0 24px 64px -16px rgba(54,82,255,0.3)" }}
            transition={{ duration: 0.4 }}
            className="rounded-xl border border-white/10 bg-white/[0.03] p-7 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] backdrop-blur-sm sm:p-9"
          >
            <div className="mb-6 flex w-full items-center justify-between gap-3">
              {view !== VIEW.LOGIN && (
                <motion.button
                  type="button"
                  onClick={() => setView(VIEW.LOGIN)}
                  whileHover={{ x: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="group flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11px] font-medium text-slate-400 transition-all duration-200 hover:border-[#5B7CFF]/30 hover:bg-[#5B7CFF]/10 hover:text-[#AAB8FF] focus:outline-none focus:ring-2 focus:ring-[#5B7CFF]/30"
                >
                  <LuArrowLeft
                    size={13}
                    className="transition-transform duration-200 group-hover:-translate-x-0.5"
                  />
                  <span>Back to login</span>
                </motion.button>
              )}

               <p className="min-w-0 truncate font-['IBM_Plex_Mono'] text-[10px] font-medium uppercase tracking-[0.18em] text-[#5B7CFF] sm:text-[11px] sm:tracking-[0.25em]">
                KSAS <span className="mx-1 text-white/30">·</span> Inventory
                Access
              </p>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={`${view}-header`}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="mb-7"
              >
                <h1 className="text-2xl font-semibold tracking-tight text-white">
                  {copy.title}
                </h1>
                <p className="mt-1.5 text-sm text-slate-400">{copy.subtitle}</p>
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {view === VIEW.LOGIN && (
                <motion.div
                  key="login"
                  variants={viewSwitchVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <LoginForm
                    onSubmit={handleLogin}
                    loading={isLoading}
                    error={loginError || (error && loginErrorToMessage(error))}
                    onForgotPassword={() => setView(VIEW.FORGOT)}
                    onRegisterOrg={() => setView(VIEW.REGISTER)}
                  />
                </motion.div>
              )}

              {view === VIEW.FORGOT && (
                <motion.div
                  key="forgot"
                  variants={viewSwitchVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <ForgotPasswordForm
                    onSubmit={handleForgotPassword}
                    loading={forgotPasswordLoading}
                    onBack={() => setView(VIEW.LOGIN)}
                  />
                </motion.div>
              )}

              {view === VIEW.REGISTER && (
                <motion.div
                  key="register"
                  variants={viewSwitchVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <RegisterOrgForm
                    onSubmit={handleRegisterOrg}
                    loading={registerOrgLoading}
                    onBack={() => setView(VIEW.LOGIN)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <p className="mt-6 text-center text-xs text-slate-500">
            Access is limited to registered lab personnel.
          </p>
        </motion.div>
      </main>

      <Toaster />

      <style>{`
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}</style>
    </div>
  );
}
